import type { ApiError, LoginResponse, PageMeta } from '@portal/shared';

/**
 * API client. The access token lives only in memory; the refresh token is an httpOnly cookie.
 * On a 401 the client refreshes once (deduplicated across concurrent requests) and retries.
 */
let accessToken: string | null = null;
let refreshPromise: Promise<LoginResponse | null> | null = null;
let onSessionChange: ((s: LoginResponse | null) => void) | null = null;

export const setAccessToken = (t: string | null) => {
  accessToken = t;
};
export const registerSessionListener = (fn: (s: LoginResponse | null) => void) => {
  onSessionChange = fn;
};

export class HttpError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
  /** Field errors from Zod validation, if any. */
  get fieldErrors(): Record<string, string[]> {
    return ((this.details as { fieldErrors?: Record<string, string[]> })?.fieldErrors ?? {}) as Record<string, string[]>;
  }
}

export async function refreshSession(): Promise<LoginResponse | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch('/api/v1/auth/refresh', { method: 'POST', credentials: 'include' });
        if (!res.ok) return null;
        const body = await res.json();
        accessToken = body.data.accessToken;
        return body.data as LoginResponse;
      } catch {
        return null;
      } finally {
        setTimeout(() => (refreshPromise = null), 0);
      }
    })();
  }
  return refreshPromise;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, unknown>;
  form?: FormData;
  raw?: boolean;
}

function buildUrl(path: string, query?: Record<string, unknown>) {
  const url = new URL(`/api/v1${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) v.forEach((x) => url.searchParams.append(k, String(x)));
    else url.searchParams.set(k, String(v));
  }
  return url.pathname + url.search;
}

async function send(path: string, opts: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  return fetch(buildUrl(path, opts.query), {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.form ?? (opts.body !== undefined ? JSON.stringify(opts.body) : undefined),
  });
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<{ data: T; meta: PageMeta & Record<string, unknown> }> {
  let res = await send(path, opts);
  if (res.status === 401 && !path.startsWith('/auth/login')) {
    const session = await refreshSession();
    if (session) {
      onSessionChange?.(session);
      res = await send(path, opts);
    } else {
      onSessionChange?.(null);
    }
  }
  if (opts.raw) {
    if (!res.ok) throw new HttpError(res.status, 'DOWNLOAD_FAILED', 'Download failed');
    return { data: (await res.blob()) as T, meta: {} as PageMeta & Record<string, unknown> };
  }
  const body = await res.json().catch(() => ({ error: { code: 'BAD_RESPONSE', message: 'Unexpected server response' } }));
  if (!res.ok || body.error) {
    const err = (body.error ?? { code: 'HTTP_' + res.status, message: res.statusText }) as ApiError;
    throw new HttpError(res.status, err.code, err.message, err.details);
  }
  return { data: body.data as T, meta: (body.meta ?? {}) as PageMeta & Record<string, unknown> };
}

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>) => request<T>(path, { query }).then((r) => r.data),
  page: <T>(path: string, query?: Record<string, unknown>) => request<T[]>(path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body ?? {} }).then((r) => r.data),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body }).then((r) => r.data),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body }).then((r) => r.data),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }).then((r) => r.data),
  upload: <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', form }).then((r) => r.data),
  /** Downloads a file (e.g. report export) and saves it in the browser. */
  async download(path: string, query: Record<string, unknown>, fileName: string) {
    const { data } = await request<Blob>(path, { query, raw: true });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export const errorMessage = (e: unknown) =>
  e instanceof HttpError ? e.message : e instanceof Error ? e.message : 'Something went wrong';
